import { Injectable, inject } from '@angular/core';
import { arrayAdd, arrayRemove } from '@datorama/akita';
import { SortDirectionChangeEvent } from '@eg/ui';
import { CustomerConfigModel, SettingsService } from '@railmybox/api-user';
import { SortDirection, arrayNaturalSort } from '@railmybox/shared/util';
import dayjs from 'dayjs/esm';
import { catchError, map, tap, throwError } from 'rxjs';

/**
 * Minimal shape shared by every feature-specific `QuickFiltersModel` (disposition, train-visits,
 * capacity-manager, ...). Only the fields actually read by this shared service/component are declared here.
 */
export interface QuickFilterItem {
  customId?: string;
  active?: boolean;
  name?: string;
}

@Injectable()
export class QuickFiltersService {
  private readonly settingsService = inject(SettingsService);

  getFiltersList(context: string, store: any): Promise<CustomerConfigModel[]> {
    return this.settingsService
      .listCustomerConfigs(context, 'quickFilter')
      .pipe(
        map((value: CustomerConfigModel[]) => {
          const data = value.map((item) => this.mapJSONToObject(item));
          return data;
        }),
        tap((data) => {
          store.set(data);
          store.update((state) => {
            return { ...state, quickFilters: data[0]?.value || [] };
          });
        }),
        catchError((err) => {
          store.setError(err);
          return throwError(err);
        })
      )
      .toPromise();
  }

  mapObjectToJSON(value, context: string) {
    const customerConfigAdd: CustomerConfigModel = {
      context: context,
      type: 'quickFilter',
      value: JSON.stringify(value),
    };
    return customerConfigAdd;
  }

  mapJSONToObject(value) {
    const customerConfigAdd: CustomerConfigModel = {
      context: value.context,
      type: value.type,
      value: value.value ? JSON.parse(value.value.toString()) : '',
    };
    return customerConfigAdd;
  }

  onDelete(id: string, filter: CustomerConfigModel, store: any, context: string): Promise<CustomerConfigModel> {
    return this.settingsService
      .createOrUpdateCustomerConfig(context, filter)
      .pipe(
        tap((saved) => {
          const data = this.mapJSONToObject(saved);
          store.update(id, data);
          store.update((state) => {
            return {
              ...state,
              quickFilters: arrayRemove(state.quickFilters, id, 'customId'),
            };
          });
        })
      )
      .toPromise();
  }

  async onUpdate(id, filterList, store: any): Promise<void> {
    return this.updateFilter(id, filterList).then((_) => {
      const data = this.mapJSONToObject(_);
      store.update((state) => {
        return { ...state, quickFilters: data.value };
      });
    });
  }

  updateFilter(id: string, filter: CustomerConfigModel): Promise<CustomerConfigModel> {
    return this.settingsService.createOrUpdateCustomerConfig(filter.context, filter).toPromise();
  }

  maxActiveFiltersCheck(list: QuickFilterItem[], activation: boolean, maxLength: number, editFilter?: QuickFilterItem): boolean {
    if (!list || !list.length) return false;

    // Exclude the currently edited filter (if any)
    const filtered = editFilter ? list.filter((item) => item !== editFilter) : list;

    const activeCount = filtered.filter((item) => item.active === true).length;

    // If user tries to activate while already at max, block
    return activation === true && activeCount >= maxLength;
  }

  sorting<T>(event: SortDirectionChangeEvent<T>, query: any, store: any) {
    let sortedList: T[] = [];
    const list = query.getFilters() as T[];

    if (event.valueGetterFn === undefined) {
      sortedList = arrayNaturalSort(list, event.key as keyof T, event.sortDirection);
    } else {
      const options: Intl.CollatorOptions = {
        numeric: true,
        sensitivity: 'base',
      };

      sortedList = [...list].sort((a, b) => {
        return event.sortDirection === SortDirection.ASC
          ? event.valueGetterFn(a)?.toString().localeCompare(event.valueGetterFn(b), undefined, options)
          : event.valueGetterFn(b)?.toString().localeCompare(event.valueGetterFn(a), undefined, options);
      });
    }

    store.update((state) => {
      return {
        ...state,
        quickFilters: sortedList,
      };
    });
  }

  async onSave(customerConfig, newItem, store: any): Promise<void> {
    await this.createFilter(customerConfig).then((_) => {
      store.update((state) => {
        return { ...state, quickFilters: arrayAdd(state.quickFilters, newItem) };
      });
    });
  }

  createFilter(filter: CustomerConfigModel): Promise<CustomerConfigModel> {
    return this.settingsService.createOrUpdateCustomerConfig(filter.context, filter).toPromise();
  }

  getDateRangeTrains() {
    return {
      lastSevenDays: {
        validFrom: dayjs().subtract(7, 'day').startOf('day').format('YYYY-MM-DD'),
        validTo: dayjs().subtract(1, 'day').endOf('day').format('YYYY-MM-DD'),
      },
      lastWeek: {
        validFrom: dayjs().subtract(1, 'week').startOf('week').add(1, 'day').format('YYYY-MM-DD'),
        validTo: dayjs().subtract(1, 'week').endOf('week').add(1, 'day').format('YYYY-MM-DD'),
      },
      yesterday: {
        validFrom: dayjs().subtract(1, 'day').startOf('day').format('YYYY-MM-DD'),
        validTo: dayjs().subtract(1, 'day').endOf('day').format('YYYY-MM-DD'),
      },
      today: {
        validFrom: dayjs().startOf('day').format('YYYY-MM-DD'),
        validTo: dayjs().endOf('day').format('YYYY-MM-DD'),
      },
      tomorrow: {
        validFrom: dayjs().add(1, 'day').startOf('day').format('YYYY-MM-DD'),
        validTo: dayjs().add(1, 'day').endOf('day').format('YYYY-MM-DD'),
      },
      thisWeek: {
        validFrom: dayjs().startOf('week').add(1, 'day').format('YYYY-MM-DD'),
        validTo: dayjs().endOf('week').add(1, 'day').format('YYYY-MM-DD'),
      },
      nextTwoDays: {
        validFrom: dayjs().add(1, 'day').startOf('day').format('YYYY-MM-DD'),
        validTo: dayjs().add(2, 'day').endOf('day').format('YYYY-MM-DD'),
      },
      nextThreeDays: {
        validFrom: dayjs().add(1, 'day').startOf('day').format('YYYY-MM-DD'),
        validTo: dayjs().add(3, 'day').endOf('day').format('YYYY-MM-DD'),
      },
      nextSevenDays: {
        validFrom: dayjs().add(1, 'day').startOf('day').format('YYYY-MM-DD'),
        validTo: dayjs().add(7, 'day').endOf('day').format('YYYY-MM-DD'),
      },
      nextTenDays: {
        validFrom: dayjs().add(1, 'day').startOf('day').format('YYYY-MM-DD'),
        validTo: dayjs().add(10, 'day').endOf('day').format('YYYY-MM-DD'),
      },
    };
  }
}
